import { toast } from "react-toastify";
import API from "../apiClient";
import { handleApiError } from '../Utils/errorHandler';

export const GetPolicyData = async (org_id, search_pattern, type = null) => {
  try {
    // Handle null/undefined organizationId properly
    const cleanOrgId = (org_id && org_id !== 'null' && org_id !== 'undefined') ? org_id : '';
    
    const queryParams = new URLSearchParams({ organization_id: cleanOrgId });

    // Filter by type only if explicitly provided
    if (type !== null && type !== undefined) {
      queryParams.append("type", type);
    }

    if (search_pattern) {
      queryParams.append("search_pattern", search_pattern);
    }

    const queryString = queryParams.toString();
    const response = await API.get(`/policies/?${queryString}`);
    return response;
  } catch (error) {
    throw handleApiError(error, 'Failed to get policy data', {
      organization_id: org_id,
      search_pattern,
      type
    });
  }
};

// Pass { silent: true } when 404 is an expected outcome (e.g. Index Explorer
// classifying deleted policies still present in ACS) so we don't toast-spam.
export const GetPolicyDetailByPolicyID = async (entity, organizationId, { silent = false } = {}) => {
  try {
    const params = organizationId ? { organization_id: organizationId } : {};
    const response = await API.get(`/policies/${encodeURIComponent(entity)}`, { params });
    return response;
  } catch (error) {
    if (silent) throw error;
    throw handleApiError(error, 'Failed to get policy details', { policy_id: entity });
  }
};

export const GetPolicyRelatedControls = async (policyId, organizationId) => {
  try {
    if (!organizationId) {
      throw new Error('organizationId is required to fetch related controls');
    }
    const queryParams = new URLSearchParams({ organization_id: organizationId });
    const response = await API.get(`/policies/${encodeURIComponent(policyId)}/related-controls?${queryParams.toString()}`);
    return response;
  } catch (error) {
    throw handleApiError(error, 'Failed to get related controls', {
      policy_id: policyId,
      organization_id: organizationId
    });
  }
};

// Commented out functions for reference
// export const addPolicy = async (payload) => {
//   try {
//     const response = await API.post(`/policy_center/policies`, payload);
//     return response;
//   } catch (error) {
//     throw handleApiError(error, 'Failed to add policy');
//   }
// };

// export const UpdatePolicy = async (entity, payload) => {
//   try {
//     const response = await API.put(`/policy_center/policies/${encodeURIComponent(entity)}`, payload);
//     return response;
//   } catch (error) {
//     throw handleApiError(error, 'Failed to update policy', { policy_id: entity });
//   }
// };

// export const DeletePolicy = async (entity) => {
//   try {
//     const response = await API.post(`/policy_center/policies/${encodeURIComponent(entity)}`);
//     return response;
//   } catch (error) {
//     throw handleApiError(error, 'Failed to delete policy', { policy_id: entity });
//   }
// };

// Get version history for a policy - simplified to use policy ID only
//
// ARCHITECTURE NOTE:
// Each policy version is a separate entity with its own UUID (not a parent-child relationship).
// Versions are grouped by policy name, not by a parent policy ID.
//
// Example:
//   You're viewing Version 4 with id="uuid-v4"
//   Call: GetPolicyVersionHistory("uuid-v4")
//   Backend: Looks up uuid-v4 → gets name "Security Policy" → returns ALL versions with that name
//   Result: Returns v1, v2, v3, v4, v5 (all sibling versions)
//
// You can pass ANY version's ID and get back ALL related versions!
export const GetPolicyVersionHistory = async (policyId) => {
  const response = await API.get(`/policies/${encodeURIComponent(policyId)}/versions`);
  return response;
};

// Set active version for a policy - simplified, no organization_id needed
//
// ARCHITECTURE NOTE:
// policyID is the UUID of the VERSION you want to activate (not a parent policy ID).
// Each version has its own UUID. The backend updates the policy_active_version table
// to point to the selected version's UUID.
//
// Example:
//   Current state: Version 5 is active
//   User clicks "Set as Active" on Version 2
//   Call: SetActivePolicyVersion("uuid-v2")  ← Version 2's UUID
//   Backend: Look up uuid-v2 → get name → update active_policy_id to uuid-v2
//   Result: Version 2 is now the active version!
//
// API: PUT /policies/{versionUUID}/activate
export const SetActivePolicyVersion = async (policyID) => {
  try {
    const response = await API.put(
      `/policies/${encodeURIComponent(policyID)}/activate`
    );
    return response;
  } catch (error) {
    throw handleApiError(error, 'Failed to set active policy version', {
      policy_id: policyID
    });
  }
};

export const UpdatePolicyVersion = async (policyID, version) => {
  try {
    const response = await API.put(
      `/policies/${encodeURIComponent(policyID)}/version`,
      { version }
    );
    return response;
  } catch (error) {
    throw handleApiError(error, 'Failed to update policy version number', {
      policy_id: policyID,
      version,
    });
  }
};

export const UpdatePolicyArchive = async (policyID, isArchived) => {
  try {
    const response = await API.put(
      `/policies/${encodeURIComponent(policyID)}/archive`,
      { is_archived: isArchived }
    );
    return response;
  } catch (error) {
    throw handleApiError(error, 'Failed to update archive status', {
      policy_id: policyID,
      is_archived: isArchived,
    });
  }
};

export const DeletePolicy = async (policyID) => {
  try {
    const response = await API.post(`/policies/${encodeURIComponent(policyID)}`);
    return response;
  } catch (error) {
    throw handleApiError(error, 'Failed to delete policy version', {
      policy_id: policyID,
    });
  }
};

/**
 * Download gap-summary CSV (per-policy or org-wide).
 * GET /policies/export/gap-summary?organization_id=...&policy_id=optional
 */
export const ExportGapSummaryCSV = async (organizationId, policyId = null) => {
  try {
    const params = new URLSearchParams({ organization_id: organizationId });
    if (policyId) params.append('policy_id', policyId);
    const response = await API.get(`/policies/export/gap-summary?${params.toString()}`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    link.download = policyId
      ? `gap-summaries-policy-${stamp}.csv`
      : `gap-summaries-${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return true;
  } catch (error) {
    throw handleApiError(error, 'Failed to export gap summaries', {
      organization_id: organizationId,
      policy_id: policyId,
    });
  }
};
